import { breakpoints, vars } from '@etg/wings';
import { ContentWrapper, ExpandableContent } from '@eti/components';
import { useSnowplowTracking, useSiteContext, useProperty, useSearch } from '@eti/providers';
import { css } from '@eti/styles';
import type { MutableRefObject } from 'react';
import type { UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { useInView } from 'react-intersection-observer';
import type { SearchFormValues } from '../../../../common/search-form-panel/SearchFormNewContainer';
import SearchFormNewContainer from '../../../../common/search-form-panel/SearchFormNewContainer';
import { SearchForm as LegacySearchForm } from '../../../../widgets/search-form';
import MoveToTopButton from '../MoveToTopButton';
import ResultTripDetailsHeader from './ResultTripDetailsHeader';
import { useResultPageContext } from './contexts/ResultPageContext';

const searchFormWrapperStyles = css`
  font-size: 0.875rem;
  padding-block: 16px 24px;
  width: 100%;

  @media (min-width: ${breakpoints._1024}) {
    max-width: 950px;
  }
`;

const sectionStyles = (isAlternateSearchBarEnabled: boolean) => css`
  background-color: ${isAlternateSearchBarEnabled
    ? vars.colors.page.background.secondary
    : vars.colors.page.background.primary};

  @media (min-width: ${breakpoints._768}) {
    box-shadow:
      0 4px 8px rgb(0 0 0 / 4%),
      0 0 2px rgb(0 0 0 / 6%),
      0 0 1px rgb(0 0 0 / 4%);
    width: 100%;
    z-index: 11;
  }
`;

export interface ResultSearchDetailsProps {
  searchFormWatchRef?: MutableRefObject<UseFormWatch<SearchFormValues> | null>;
  setValueRef?: MutableRefObject<UseFormSetValue<SearchFormValues> | null>;
}

const ResultSearchDetails = ({ searchFormWatchRef, setValueRef }: ResultSearchDetailsProps) => {
  const {
    closeSearchPanel,
    handleSearch,
    isSearchPanelOpen,
    rebookingData,
    searchData,
    shouldShowSkeletonOnResultPage,
    toggleSearchPanel,
  } = useResultPageContext();
  const { language } = useSiteContext();
  const { p } = useProperty();
  const { ref, inView } = useInView({ rootMargin: '1650px' });
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();
  const isAlternateSearchBarEnabled = p('Result.AlternateSearchBar.Enabled');
  const { isSearchFormOpen, setIsSearchFormOpen } = useSearch();
  const shouldShowSearchForm = isSearchPanelOpen || isSearchFormOpen;

  const handleToggleSearchBar = () => {
    if (isSnowplowTrackingEnabled && !isSearchPanelOpen) {
      sendTrackEvent({
        category: 'Results',
        action: 'Click',
        label: 'Search-bar',
      });
    }

    toggleSearchPanel();

    if (isAlternateSearchBarEnabled) {
      setIsSearchFormOpen!(!isSearchFormOpen);
    }
  };

  const onSearch = (values: boolean) => {
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Results',
        action: 'Click',
        label: 'Search-button',
      });
    }

    handleSearch(values);
    closeSearchPanel();

    if (isAlternateSearchBarEnabled) {
      setIsSearchFormOpen!(!isSearchFormOpen);
    }
  };

  const SearchForm = p('IbeClient.ReactHookForm.SearchForm.Enabled')
    ? SearchFormNewContainer
    : LegacySearchForm;

  return (
    <section
      className={sectionStyles(isAlternateSearchBarEnabled)}
      id="result-search-details-section"
      ref={ref}
    >
      <ContentWrapper>
        <ResultTripDetailsHeader isOpen={shouldShowSearchForm} onClick={handleToggleSearchBar} />
        {!shouldShowSkeletonOnResultPage && (
          <ExpandableContent
            data-testid="resultPage-searchForm-content"
            isExpanded={Boolean(shouldShowSearchForm)}
            shouldUnmountOnExit={false}
          >
            <div className={searchFormWrapperStyles}>
              <SearchForm
                language={language.code}
                onSearch={onSearch}
                rebookingData={rebookingData}
                search={searchData}
                searchFormWatchRef={searchFormWatchRef}
                setValueRef={setValueRef}
                shouldShowRebookingBanner={false}
              />
            </div>
          </ExpandableContent>
        )}
      </ContentWrapper>

      {p('Result.MoveToTopButton.Enabled') && <MoveToTopButton isVisible={!inView} />}
    </section>
  );
};

export default ResultSearchDetails;
